import api from './api';
import { Dokument, DokumentEntityTyp, DokumentTyp } from '../types';

// Lesen/Hochladen laufen als Unterressource der Entität, Download/Löschen über /dokumente/{id}.
const basePath = (entityType: DokumentEntityTyp, entityId: number): string => {
  switch (entityType) {
    case 'GRAB': return `/graeber/${entityId}/dokumente`;
    case 'KUNDE': return `/kunden/${entityId}/dokumente`;
    case 'VERTRAG': return `/vertraege/${entityId}/dokumente`;
  }
};

export const dokumentService = {
  getForEntity: async (entityType: DokumentEntityTyp, entityId: number): Promise<Dokument[]> => {
    const response = await api.get(basePath(entityType, entityId));
    return response.data;
  },

  upload: async (entityType: DokumentEntityTyp, entityId: number, file: File, typ: DokumentTyp): Promise<Dokument> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('typ', typ);
    // Content-Type bewusst nicht setzen → der Browser ergänzt die Multipart-Boundary selbst
    // (der globale Default 'application/json' aus api.ts würde das sonst kaputt machen).
    const response = await api.post(basePath(entityType, entityId), formData, {
      headers: { 'Content-Type': undefined },
    });
    return response.data;
  },

  // Lädt die Datei (mit Auth-Token) als Blob und liefert eine Object-URL für die Inline-Vorschau.
  // Der Aufrufer muss die URL nach Gebrauch via URL.revokeObjectURL freigeben.
  fetchBlobUrl: async (id: number, contentType: string): Promise<string> => {
    const response = await api.get(`/dokumente/${id}/download`, { responseType: 'blob' });
    return window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
  },

  // Lädt die Datei als Blob und stößt im Browser den Download an (Muster aus exportService).
  download: async (id: number, dateiname: string): Promise<void> => {
    const response = await api.get(`/dokumente/${id}/download`, { responseType: 'blob' });
    const disposition: string = response.headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match?.[1] || dateiname || 'dokument';

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  update: async (id: number, dateiname: string, dokumentTyp: DokumentTyp): Promise<Dokument> => {
    const response = await api.put(`/dokumente/${id}`, { dateiname, dokumentTyp });
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/dokumente/${id}`);
  },
};

export default dokumentService;
